import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Search, ClipboardCheck, Thermometer, AlertTriangle, Truck, CheckCircle2, XCircle, Clock, Download, Loader2, MessageSquarePlus } from "lucide-react";
import { toast } from "sonner";
import { ActionPlanCard } from "@/components/ActionPlan";
import { ClosuresCard, ShareButton, SharesCard, RatingHistory } from "@/components/RecordsTools";
import { api, errMsg, fmtDT, fmtD, todayISO } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Field } from "@/components/kit";
import { cn } from "@/lib/utils";
import { DemoHint } from "@/components/DemoTour";

const TYPES = [["all", "Everything"], ["checks", "Checklists"], ["temps", "Temperatures"], ["incidents", "Incidents"], ["deliveries", "Deliveries"]];
const ICON = { checks: ClipboardCheck, temps: Thermometer, incidents: AlertTriangle, deliveries: Truck };
const DAY_TONE = {
  green: "bg-emerald-200 text-emerald-950 hover:bg-emerald-300", amber: "bg-amber-200 text-amber-950 hover:bg-amber-300",
  red: "bg-rose-200 text-rose-950 hover:bg-rose-300", in_progress: "bg-sky-200 text-sky-950 hover:bg-sky-300",
  empty: "bg-white text-slate-500 ring-1 ring-inset ring-slate-200 hover:bg-slate-50", future: "bg-transparent text-slate-300",
  closed: "bg-slate-200 text-slate-500 hover:bg-slate-300 [background-image:repeating-linear-gradient(135deg,transparent_0_6px,rgba(255,255,255,.55)_6px_9px)]",
};

function RecordRow({ r }) {
  const I = ICON[r.type];
  return (
    <Link to={r.link} data-testid={`record-${r.type}-${r.id}`} className="flex gap-3 border-b border-slate-100 px-4 py-3 transition-colors duration-150 last:border-0 hover:bg-slate-50">
      <I className={cn("mt-0.5 h-4 w-4 shrink-0", r.flag ? "text-rose-600" : "text-emerald-600")} />
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-slate-900">{r.title}{r.flag && <span className="ml-2 rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold uppercase text-rose-700">Needs attention</span>}</span>
        <span className="block truncate text-xs text-slate-500">{r.detail}</span>
      </span>
      <span className="shrink-0 text-right text-xs text-slate-500">{fmtDT(r.at)}<span className="block">{r.by}</span></span>
    </Link>
  );
}

function SearchTab({ venueId, initialDate }) {
  const [f, setF] = useState({ q: "", type: "all", date_from: initialDate || "", date_to: initialDate || "" });
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const run = async (e) => {
    e?.preventDefault();
    setErr("");
    try {
      const params = Object.fromEntries(Object.entries({ ...f, venue_id: venueId }).filter(([, v]) => v));
      const { data } = await api.get("/records", { params });
      setRows(data);
    } catch (x) {
      setErr(errMsg(x));
    }
  };
  useEffect(() => {
    if (venueId) run();
  }, [venueId]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div>
      <Card className="mb-6">
        <form onSubmit={run} className="grid gap-4 md:grid-cols-[2fr_1fr_1fr_1fr_auto] md:items-end">
          <Field label="Search what was recorded"><input data-testid="records-search-input" className="ka-input" placeholder="e.g. walk-in fridge, Sam, cleaning, Brakes" value={f.q} onChange={set("q")} /></Field>
          <Field label="Type"><select data-testid="records-type-select" className="ka-input" value={f.type} onChange={set("type")}>{TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Field>
          <Field label="From"><input data-testid="records-from-input" type="date" className="ka-input" value={f.date_from} max={todayISO()} onChange={set("date_from")} /></Field>
          <Field label="To"><input data-testid="records-to-input" type="date" className="ka-input" value={f.date_to} max={todayISO()} onChange={set("date_to")} /></Field>
          <button data-testid="records-search-button" className="ka-press inline-flex items-center justify-center gap-1.5 rounded-full bg-emerald-600 px-6 py-3 text-sm font-semibold text-white hover:bg-emerald-700"><Search className="h-4 w-4" />Search</button>
        </form>
      </Card>
      {err && <div data-testid="records-error" className="mb-4 text-sm text-rose-700">{err}</div>}
      {rows && <div data-testid="records-result-count" className="mb-2 text-sm text-slate-500">{rows.length}{rows.length >= 500 ? "+" : ""} record{rows.length === 1 ? "" : "s"}</div>}
      <Card className="p-0 sm:p-0">
        <div data-testid="records-results">
          {(rows || []).map((r) => <RecordRow key={`${r.type}-${r.id}`} r={r} />)}
          {rows && !rows.length && <div className="px-4 py-10 text-center text-sm text-slate-500">Nothing matches. Try another word or a wider date range.</div>}
        </div>
      </Card>
    </div>
  );
}

const STATUS_ICON = { done: [CheckCircle2, "text-emerald-600", "Done"], exception: [AlertTriangle, "text-amber-600", "Done with issues"], missed: [XCircle, "text-rose-600", "Not done"], due: [Clock, "text-sky-600", "Still due today"] };

const PRESETS = ["Closed today", "No service", "Equipment out of use", "Staff shortage", "Done on paper"];

function MissedNote({ venueId, date, c, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [reason, setReason] = useState(c.note?.reason || "");
  const [busy, setBusy] = useState(false);
  const submit = async (text) => {
    setBusy(true);
    try {
      await api.put("/records/missed-note", { venue_id: venueId, template_id: c.template_id, date, reason: text });
      toast.success("Reason saved");
      setEditing(false);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const save = (e) => {
    e.preventDefault();
    submit(reason);
  };
  if (editing) return (
    <form onSubmit={save} className="mt-1.5 flex flex-col gap-2">
      <div className="flex flex-wrap gap-1.5">{PRESETS.map((p, i) => <button type="button" key={p} disabled={busy} data-testid={`missed-note-preset-${i}-${c.template_id}`} onClick={() => submit(p)} className="ka-press rounded-full border border-slate-300 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 hover:border-emerald-600 hover:text-emerald-700">{p}</button>)}</div>
      <textarea data-testid={`missed-note-input-${c.template_id}`} required minLength={3} maxLength={500} rows={2} className="ka-input text-sm" placeholder="e.g. Kitchen closed for deep clean" value={reason} onChange={(e) => setReason(e.target.value)} />
      <div className="flex gap-2">
        <button data-testid={`missed-note-save-${c.template_id}`} disabled={busy} className="ka-press rounded-full bg-emerald-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">{busy ? "Saving…" : "Save reason"}</button>
        <button type="button" data-testid={`missed-note-cancel-${c.template_id}`} onClick={() => { setReason(c.note?.reason || ""); setEditing(false); }} className="text-xs font-semibold text-slate-500">Cancel</button>
      </div>
    </form>
  );
  return (
    <div className="mt-1 text-xs">
      {c.note && <div data-testid={`missed-note-${c.template_id}`} className="rounded-lg bg-amber-50 px-2 py-1 text-amber-950"><b>Reason:</b> {c.note.reason} <span className="text-amber-700">· {c.note.by}, {fmtDT(c.note.at)}</span></div>}
      <button data-testid={`missed-note-edit-${c.template_id}`} onClick={() => setEditing(true)} className="mt-1 inline-flex items-center gap-1 font-semibold text-emerald-700 hover:underline"><MessageSquarePlus className="h-3.5 w-3.5" />{c.note ? "Edit reason" : "Add reason"}</button>
    </div>
  );
}

function DayPanel({ venueId, date, onSearch, onChanged }) {
  const [d, setD] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    setD(null);
    api.get("/records/day", { params: { venue_id: venueId, date } }).then((r) => setD(r.data)).catch(() => setD({ checks: [], records: [] }));
  }, [venueId, date, tick]);
  const saved = () => {
    setTick((t) => t + 1);
    onChanged();
  };
  return (
    <Card data-testid="calendar-day-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="font-display text-xl font-extrabold">{fmtD(date)}</div>
        <button data-testid="day-open-search-button" onClick={() => onSearch(date)} className="text-sm font-semibold text-emerald-700 hover:underline">Search this day</button>
      </div>
      {!d ? <p className="mt-4 text-sm text-slate-500">Loading…</p> : <>
        {d.closed_reason && <div data-testid="day-closed-banner" className="mt-4 rounded-xl bg-slate-100 px-3 py-2 text-sm text-slate-700"><b>Closed</b> · {d.closed_reason}. No checks were due.</div>}
        <div className="ka-eyebrow mt-5 mb-2">Checklists</div>
        <ul className="space-y-2">
          {d.checks.map((c) => {
            const [I, tone, label] = STATUS_ICON[c.status];
            return <li key={c.template_id} data-testid={`day-check-${c.template_id}`} className="flex items-start gap-2 text-sm"><I className={cn("mt-0.5 h-4 w-4 shrink-0", tone)} /><span className="flex-1"><b>{c.name}</b>{c.due_time && <span className="text-slate-500"> · due {c.due_time}</span>}<span className="block text-xs text-slate-500">{label}{c.by && ` · ${c.by}, ${fmtDT(c.at)}`}</span>{c.status === "missed" && <MissedNote venueId={venueId} date={date} c={c} onSaved={saved} />}</span></li>;
          })}
          {!d.checks.length && !d.closed_reason && <li className="text-sm text-slate-500">No checklists were due.</li>}
        </ul>
        <div className="ka-eyebrow mt-6 mb-2">Other records</div>
        <div className="-mx-5 border-t border-slate-100 sm:-mx-6">
          {d.records.map((r) => <RecordRow key={`${r.type}-${r.id}`} r={r} />)}
          {!d.records.length && <p className="px-6 py-3 text-sm text-slate-500">No temperatures, incidents or deliveries.</p>}
        </div>
      </>}
    </Card>
  );
}

function monthStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function CalendarTab({ venueId, onSearch }) {
  const [month, setMonth] = useState(() => monthStr(new Date()));
  const [data, setData] = useState(null);
  const [day, setDay] = useState(todayISO());
  const [tick, setTick] = useState(0);
  const [pdfBusy, setPdfBusy] = useState(false);
  useEffect(() => {
    if (venueId) api.get("/records/calendar", { params: { venue_id: venueId, month } }).then((r) => setData(r.data));
  }, [venueId, month, tick]);
  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const { data: blob, headers } = await api.get("/records/calendar.pdf", { params: { venue_id: venueId, month }, responseType: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = (headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] || `kitchenaudit-${month}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Couldn't create the PDF. Please try again.");
    } finally {
      setPdfBusy(false);
    }
  };
  const shift = (n) => {
    const [y, m] = month.split("-").map(Number);
    setMonth(monthStr(new Date(y, m - 1 + n, 1)));
  };
  const lead = data ? (new Date(`${month}-01T12:00:00`).getDay() + 6) % 7 : 0;
  const label = new Date(`${month}-01T12:00:00`).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
      <Card>
        <div className="mb-4 flex items-center justify-between">
          <button data-testid="calendar-prev-month" onClick={() => shift(-1)} className="ka-press rounded-full p-2 hover:bg-slate-100"><ChevronLeft className="h-5 w-5" /></button>
          <div data-testid="calendar-month-label" className="font-display text-xl font-extrabold">{label}</div>
          <button data-testid="calendar-next-month" disabled={month >= monthStr(new Date())} onClick={() => shift(1)} className="ka-press rounded-full p-2 hover:bg-slate-100 disabled:opacity-30"><ChevronRight className="h-5 w-5" /></button>
        </div>
        <div className="grid grid-cols-7 gap-1.5 text-center font-mono text-[10px] uppercase tracking-wider text-slate-400">{["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d}>{d}</div>)}</div>
        <div data-testid="calendar-grid" className="mt-1.5 grid grid-cols-7 gap-1.5">
          {Array.from({ length: lead }).map((_, i) => <div key={`l${i}`} />)}
          {(data?.days || []).map((d) => (
            <button key={d.date} data-testid={`calendar-day-${d.date}`} data-status={d.status} title={d.closed_reason || undefined} disabled={d.status === "future"} onClick={() => setDay(d.date)}
              className={cn("ka-press flex aspect-square flex-col items-center justify-center rounded-xl text-sm font-bold transition-colors duration-150", DAY_TONE[d.status], day === d.date && "ring-2 ring-slate-900")}>
              {Number(d.date.slice(8))}
              {d.checks_due > 0 && <span className="text-[10px] font-medium opacity-80">{d.checks_done}/{d.checks_due}</span>}
            </button>
          ))}
        </div>
        <div className="mt-4 flex flex-wrap gap-3 text-xs text-slate-600">
          {[["green", "All done"], ["amber", "Some missed or issues"], ["red", "Nothing done"], ["in_progress", "Today, in progress"], ["closed", "Closed"]].map(([k, l]) => <span key={k} className="flex items-center gap-1.5"><span className={cn("h-3 w-3 rounded", DAY_TONE[k].split(" ")[0])} />{l}</span>)}
        </div>
        <div className="mt-5 flex flex-wrap gap-2">
          <span className="relative inline-flex">
          <button data-testid="calendar-download-pdf-button" disabled={pdfBusy || !venueId} onClick={downloadPdf} className="ka-press inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">{pdfBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}{pdfBusy ? "Creating PDF…" : `Download ${label} PDF`}</button>
          <DemoHint label="New" testid="demo-hint-pdf" tip="The whole month day by day, ready for an EHO visit" />
          </span>
          <ShareButton venueId={venueId} month={month} label={label} />
        </div>
      </Card>
      {venueId && <DayPanel venueId={venueId} date={day} onSearch={onSearch} onChanged={() => setTick((t) => t + 1)} />}
      {venueId && <ClosuresCard venueId={venueId} onChanged={() => setTick((t) => t + 1)} />}
      {venueId && <SharesCard venueId={venueId} />}
      {venueId && <ActionPlanCard venueId={venueId} />}
      {venueId && <RatingHistory venueId={venueId} />}
    </div>
  );
}

export default function Records() {
  const { venueId, venue } = useAuth();
  const [tab, setTab] = useState("calendar");
  const [searchDate, setSearchDate] = useState("");
  const openSearch = (d) => {
    setSearchDate(d);
    setTab("search");
  };
  return (
    <div>
      <PageHeader eyebrow={venue?.name || "Records"} title="Records" subtitle="Search everything that's been recorded, or pick a day to see what was done and what wasn't." />
      <div className="mb-6 flex gap-2">
        {[["calendar", "Calendar"], ["search", "Search"]].map(([k, l]) => <button key={k} data-testid={`records-tab-${k}`} onClick={() => setTab(k)} className={`ka-press rounded-full px-4 py-1.5 text-sm font-semibold ${tab === k ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-100"}`}>{l}</button>)}
      </div>
      {tab === "calendar" ? <CalendarTab venueId={venueId} onSearch={openSearch} /> : <SearchTab key={`${venueId}-${searchDate}`} venueId={venueId} initialDate={searchDate} />}
    </div>
  );
}
